import { GenericService } from 'services/genericService';
import { Observable } from 'rxjs';
import { IGetProductsByParentCategory } from 'utils/api/endpointsGenerators/eshopApi/products/getProductsByParentCategory';
import { getByProductId, getProductsByParentCategory, getCatEntry } from 'utils/api/queries/eshopApi';
import { map, share } from 'rxjs/operators';
import { IInventoryByProductId } from 'utils/api/models/eshop/inventory';
import { roundMoney } from 'utils/converters/roundMoney';
import { IPriceByCatEntry } from 'utils/api/models/eshop/priceByCatEntry';
import {
	IProductById,
	IProductByIdItem,
	IProductComponents,
	IProductsByCategoryId,
} from 'utils/api/models/eshop/products';
import { IAddItemsToCartPayload, IValidateCart } from 'utils/api/payloads/eshopApi';
import { IValidateOffer } from 'utils/api/models/eshop/validateCart';
import { IGuestLogIn } from 'utils/api/models/eshop/guestLogIn';
import { IViewCart } from 'utils/api/models/eshop/viewCart';
import { CAPACITY_ATTRIBUTE, COLOR_ATTRIBUTE } from 'utils/api/models/eshop/singleProduct';
import {
	getInventoryByProductId,
	validateCart,
	addItemsToCart,
	getOmantelIdentity,
	clearAllCartItems,
	viewCart,
	removeSingleCartItem,
} from 'utils/api/queries/eshopWcsApi';

export class EshopProductsService extends GenericService {
	public static getInventoryByProductId(productId: string): Observable<IInventoryByProductId> {
		return getInventoryByProductId(productId).call();
	}

	public static getProductsByParentCategory(
		request: IGetProductsByParentCategory
	): Observable<IProductsByCategoryId> {
		return getProductsByParentCategory(request).call();
	}

	public static getProductById(request: any): Observable<IProductById> {
		return getByProductId(request).call();
	}

	public static getCatEntry({ catEntryId }: { catEntryId: string }): Observable<IPriceByCatEntry> {
		return getCatEntry({ catEntryId }).call();
	}

	public static validateCart(request: IValidateCart): Observable<IValidateOffer> {
		return validateCart(request).call();
	}

	public static getOmantelIdentity(): Observable<IGuestLogIn> {
		return getOmantelIdentity().call();
	}

	public static addItemsToCart(request: IAddItemsToCartPayload[]): Observable<any> {
		return addItemsToCart(request).call();
	}

	public static viewCart(
		WCTrustedToken?: string,
		WCToken?: string,
		WCPersonalization?: string
	): Observable<IViewCart> {
		return viewCart(WCTrustedToken, WCToken, WCPersonalization).call();
	}

	public static clearAllCartItems(): Observable<any> {
		return clearAllCartItems().call();
	}

	public static removeSingleCartItem(orderItemId: string): Observable<any> {
		return removeSingleCartItem(orderItemId).call();
	}

	public static getAvailableProductComponents({ product, items }: any): Observable<IProductComponents> {
		return this.getInventoryByProductId(product.uniqueID).pipe(
			map(({ inventory }) => {
				const flatColorsInventory: any[] = [];
				const flatCapacityInventory: any[] = [];

				const _inventory: any = {};
				product.inventory = inventory;

				if ((product.isItem || product.isGiftCard) && !product.colorAttributeId) {
					const attrId = product.attributes?.find((attr: any) => attr.identifier === 'DEF_ITEM_TYPE')?.id;
					if (attrId && inventory?.[attrId]?.[0]?.value) {
						if (Number(inventory[attrId][0].value) > 0) {
							product.availability = true;
						}
						product.price = Number(inventory[attrId][0].offerPrice ?? 0);
					}
					return {
						availableCapacity: [],
						availableColors: [],
						product,
						inventory,
					};
				}

				try {
					if (product.colorAttributeId && inventory[product.colorAttributeId]) {
						_inventory[product.colorAttributeId] = [...inventory[product.colorAttributeId]]
							?.slice()
							?.sort((a, b) => {
								if (Number(a.offerPrice) > Number(b.offerPrice)) return 1;
								if (Number(a.offerPrice) === Number(b.offerPrice)) return 0;
								return -1;
							})
							.map((item) => {
								const index = flatColorsInventory.findIndex((inv) => item.attrval === inv.attrval);
								if (Number(item.value) < 0) {
									item.value = '0';
								}
								if (index >= 0) {
									flatColorsInventory[index].value = String(
										Number(flatColorsInventory[index].value) + Number(item.value)
									);
								} else {
									flatColorsInventory.push({ ...item });
								}
								return item;
							});
					}
					if (product.capacityAttributeId && inventory[product.capacityAttributeId]) {
						_inventory[product.capacityAttributeId] = [...inventory[product.capacityAttributeId]]?.map(
							(item) => {
								const index = flatCapacityInventory.findIndex((inv) => item.attrval === inv.attrval);
								if (index >= 0) {
									flatCapacityInventory[index].value = String(
										Number(flatCapacityInventory[index].value) + Number(item.value)
									);
								} else {
									flatCapacityInventory.push({ ...item });
								}
								return item;
							}
						);
					}
				} catch (e) {
					console.log(e);
				}

				const availableCapacity = product[
					product.isGiftCard || product.isDigitalProduct ? 'availableValues' : 'availableCapacity'
				].map((item: any) => ({
					id: item.id,
					label: item.value,
					available: Number(flatCapacityInventory.find((inv) => inv.attrval === item.id)?.value) > 0,
					price: (() => {
						const {
							offerPrice = '0',
							listPrice = '0',
							taxablePrice = '0',
							vatPercent = '0',
							vatValue = '0',
						} = flatCapacityInventory.find((inv) => inv.attrval === item.id) || {};
						return {
							offerPrice,
							listPrice,
							taxablePrice,
							vatPercent,
							vatValue,
						};
					})(),
				}));

				const labelType = product.attributes?.find(
					(attribute: any) => attribute.identifier === 'DEVICE_LABEL_TYPE'
				);
				const isAppleWatch = labelType?.values[0].identifier === 'APPLE_WATCH';

				const availableColors = product[
					product.isGiftCard || product.isDigitalProduct ? 'availableCountries' : 'colors'
				].map((item: any) => {
					const availableSomeCountry = product.isGiftCard
						? flatColorsInventory.some((item) => Number(item.value || '') > 0)
						: false;

					const inventoryProduct = flatColorsInventory.find((inv) => inv.attrval === item.id);
					const {
						listPrice = '0',
						offerPrice = '0',
						taxablePrice = '0',
						vatValue = '0',
						vatPercent = '0',
						thumbnail = '',
						fullImage = '',
					} = inventoryProduct || {};
					product.price = Number(offerPrice);
					if (Number(inventoryProduct?.value) > 0 && !isAppleWatch) {
						product.availability = true;
					} else if (isAppleWatch) product.availability = true;
					return {
						...item,
						listPrice,
						offerPrice,
						taxablePrice,
						vatValue: roundMoney(vatValue),
						vatPercent,
						available: availableSomeCountry || Number(inventoryProduct?.value) > 0,
						thumbnail,
						fullImage,
						availableCapacity: product[
							product.isGiftCard || product.isDigitalProduct ? 'availableValues' : 'availableCapacity'
						].map((capacity: any) => {
							const color = inventory[product.colorAttributeId ?? '']?.find((inv) => {
								if (items && items?.length > 0 && isAppleWatch) {
									const findedItem = items.find((item: any) => item.partNumber === inv.sku);
									const findedColorName = findedItem?.attributes.find(
										(attribute: any) => attribute.identifier === 'DEF_Color'
									)?.values[0].identifier;
									return inv.attrval === item.id && findedColorName === capacity.parsedIdentifier;
								} else {
									return (
										inv.attrval === item.id &&
										(inv.sku.includes(
											capacity.parsedIdentifier?.replace('GB', '').replaceAll(' ', '')
										) ||
											inv.sku.includes('Huawei_Mate_XS_2'))
									);
								}
							});
							if (Number(color?.value) > 0) {
								return capacity.id;
							}
							return null;
						}),
					};
				});

				const additionalFilterAttributes = getAdditionalFilterAttributes({
					attributes: product.attributes ?? [],
					available: availableColors.some((col: any) => col.available),
				});
				try {
					if (additionalFilterAttributes && product.attributes) {
						product.attributes.push(additionalFilterAttributes);
					}
				} catch {}

				product.availableColors = availableColors;

				return { availableColors, availableCapacity, inventory: _inventory, product };
			}),
			share()
		);
	}
}

function isString(value: any | string): boolean {
	return typeof value === 'string' || value instanceof String;
}

const getAdditionalFilterAttributes = ({
	attributes,
	available,
}: {
	attributes: any[];
	available: boolean;
}): any | undefined => {
	const identifiers: string[] = [];
	const eshopMarketingTag: any | string = attributes.find((attr) => attr.identifier === 'ESHOP_MARKETING_TAG')
		?.values[0].value;
	if (attributes.find((attr) => attr.identifier === '5G')?.values[0].value === 'Yes') {
		identifiers.push('5G_READY');
	}
	if (attributes.find((attr) => attr.identifier === 'PREORDER')?.values[0].value === 'TRUE') {
		identifiers.push('PREORDER');
	}

	if (isString(eshopMarketingTag)) {
		identifiers.push(eshopMarketingTag);
	}
	if (available) {
		identifiers.push('AVAILABLE');
	}
	if (identifiers.length) {
		return {
			comparable: 'false',
			displayable: 'false',
			facetable: 'false',
			searchable: 'false',
			swatchable: 'false',
			storeDisplay: 'false',
			usage: 'Descriptive',
			name: 'ADDITIONAL_FILTER',
			id: '123',
			identifier: 'ADDITIONAL_FILTER',
			key: '123',
			merchandisable: 'false',
			values: [
				{
					id: '',
					identifier: identifiers,
					sequence: '',
					value: identifiers,
					image1: '',
				},
			],
		};
	}
};

export const getFullDetailsNameOfDeviceByPartNumber = (
	partNumber: string,
	cartDeviceName: string,
	products: IProductByIdItem[]
) => {
	const product = products.find((prod) => prod?.partNumber === partNumber);
	if (!!product) {
		const productName = cartDeviceName;
		const productCapacity =
			(product?.attributes || []).find((attributte) => attributte.identifier === CAPACITY_ATTRIBUTE)?.values?.[0]
				?.value || '';
		const productColor =
			(product?.attributes || []).find((attributte) => attributte.identifier === COLOR_ATTRIBUTE)?.values?.[0]
				?.value || '';
		return `${productName}${productCapacity ? ` / ${productCapacity}` : ''}${
			productColor ? ` / ${productColor}` : ''
		}`;
	}
	return '';
};
